import { useState } from 'react';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtTimestamp(isoString) {
  if (!isoString) return 'never';
  const then = new Date(isoString);
  if (Number.isNaN(then.getTime())) return 'never';
  return then.toLocaleString('en-GB');
}

function CensusBucket({ title, bucket, testid }) {
  if (!bucket) return null;
  const totals = Object.entries(bucket.total_by_currency || {});
  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{title}</span>
        <Badge className={bucket.count > 0 ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'}>
          {bucket.count}
        </Badge>
      </div>
      {totals.length > 0 && (
        <div className="text-xs text-muted-foreground">
          {totals.map(([cur, amt]) => `${amt} ${cur}`).join(', ')}
        </div>
      )}
      {bucket.invoices?.length > 0 && (
        <div className="border rounded overflow-x-auto max-h-56 overflow-y-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50 sticky top-0">
                <th className="text-left px-2 py-1 font-medium">Invoice #</th>
                <th className="text-left px-2 py-1 font-medium">Vendor</th>
                <th className="text-left px-2 py-1 font-medium">Amount</th>
                <th className="text-left px-2 py-1 font-medium">Entity</th>
              </tr>
            </thead>
            <tbody>
              {bucket.invoices.map((inv) => (
                <tr key={inv.id} className="border-b last:border-0">
                  <td className="px-2 py-1">{inv.invoice_number || inv.id}</td>
                  <td className="px-2 py-1">{inv.supplier_client_name || '-'}</td>
                  <td className="px-2 py-1">{inv.amount != null ? `${inv.amount} ${inv.currency || ''}` : '-'}</td>
                  <td className="px-2 py-1">{inv.entity_id || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SurfaceRows({ surface }) {
  const rows = surface.rows;
  // Count-only surfaces (subscription detection, intercompany) have no
  // `rows` array -- they report count_total/count_rejected directly.
  if (!rows) {
    return (
      <tr className="border-b last:border-0" data-testid={`exposure-surface-${surface.surface}`}>
        <td className="px-2 py-1 font-medium">{surface.description}</td>
        <td className="px-2 py-1 text-muted-foreground">count-only</td>
        <td className="px-2 py-1">-</td>
        <td className="px-2 py-1">{surface.count_total}</td>
        <td className="px-2 py-1">{surface.count_rejected}</td>
        <td className="px-2 py-1" colSpan={3}>{surface.scope_note}</td>
      </tr>
    );
  }
  return rows.map((row, i) => (
    <tr key={`${surface.surface}-${row.bucket}-${row.currency}`} className="border-b last:border-0" data-testid={i === 0 ? `exposure-surface-${surface.surface}` : undefined}>
      {i === 0 && (
        <td className="px-2 py-1 font-medium align-top" rowSpan={rows.length}>
          {surface.description}
          {surface.scope_note && <div className="text-[10px] text-muted-foreground font-normal mt-1">{surface.scope_note}</div>}
        </td>
      )}
      <td className="px-2 py-1">{row.bucket}</td>
      <td className="px-2 py-1">{row.currency}</td>
      <td className="px-2 py-1">{row.count_total}</td>
      <td className="px-2 py-1">{row.count_rejected}</td>
      <td className="px-2 py-1">{row.current_total}</td>
      <td className="px-2 py-1 text-red-600 dark:text-red-400">-{row.rejected_amount}</td>
      <td className="px-2 py-1 font-medium">{row.would_be_total}</td>
      <td className="px-2 py-1">
        {row.reconciles ? (
          <Badge variant="secondary" className="text-[10px] bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">reconciles</Badge>
        ) : (
          <Badge className="text-[10px] bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
            <AlertTriangle className="h-3 w-3 mr-1" />mismatch
          </Badge>
        )}
      </td>
    </tr>
  ));
}

export function RejectedInvoiceExposurePanel() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await diagnosticsAPI.rejectedInvoiceExposure();
      setReport(data);
      const notReconciling = (data.restatement_deltas?.surfaces || []).filter((s) => s.all_reconcile === false);
      if (notReconciling.length > 0) {
        toast.error(`${notReconciling.length} surface(s) failed their own reconciliation check -- see the mismatch badge`);
      } else {
        toast.success('Rejected-invoice exposure report loaded');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const census = report?.damage_census;
  const surfaces = report?.restatement_deltas?.surfaces || [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Rejected Invoice Exposure</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Read-only. Sizes the restatement adding "rejected" to config.EXCLUDED_REVIEW_STATES
          (exclusion-audit slice 3) would cause, per money-wrong surface -- current total, how
          much of it is rejected-invoice money, and the would-be total, with a reconciliation
          check per row. Also a damage census for the live-push path a prior commit closed:
          rejected invoices already matched or already pushed to Pennylane, which may need a
          manual credit-note reversal (not done here). Nothing here writes anything.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="rejected-exposure-load" onClick={load} disabled={loading} size="sm">
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            {report ? 'Refresh' : 'Load report'}
          </Button>
          {report?.generated_at && (
            <span className="text-xs text-muted-foreground">Generated: {fmtTimestamp(report.generated_at)}</span>
          )}
        </div>

        {census && (
          <div className="space-y-2">
            <span className="text-sm font-medium" data-testid="rejected-exposure-census-header">Damage census -- {census.total_rejected_invoices} rejected invoice(s) total</span>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <CensusBucket title="Pushed to Pennylane" bucket={census.pushed_to_pennylane} testid="rejected-exposure-census-pushed" />
              <CensusBucket title="Confirmed, not pushed" bucket={census.confirmed_not_pushed} testid="rejected-exposure-census-confirmed" />
              <CensusBucket title="Suggested, not confirmed" bucket={census.suggested_not_confirmed} testid="rejected-exposure-census-suggested" />
            </div>
          </div>
        )}

        {surfaces.length > 0 && (
          <div className="space-y-2">
            <span className="text-sm font-medium">Restatement deltas by surface</span>
            <div className="border rounded overflow-x-auto" data-testid="rejected-exposure-surfaces-table">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1 font-medium">Surface</th>
                    <th className="text-left px-2 py-1 font-medium">Bucket</th>
                    <th className="text-left px-2 py-1 font-medium">Currency</th>
                    <th className="text-left px-2 py-1 font-medium">N</th>
                    <th className="text-left px-2 py-1 font-medium">N rejected</th>
                    <th className="text-left px-2 py-1 font-medium">Current</th>
                    <th className="text-left px-2 py-1 font-medium">Δ rejected</th>
                    <th className="text-left px-2 py-1 font-medium">Would-be</th>
                    <th className="text-left px-2 py-1 font-medium">Check</th>
                  </tr>
                </thead>
                <tbody>
                  {surfaces.map((surface) => <SurfaceRows key={surface.surface} surface={surface} />)}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
